<template>
  <span class="app-badge" :class="[toneVariantClass, sizeClass]">
    <icon v-if="icon" :path="icon" :size="size === 'md' ? 14 : 12" />
    <slot>{{ label }}</slot>
  </span>
</template>

<script setup>
  /**
   * AppBadge — small inline status/label pill.
   *
   * Replaces the legacy `Pill` (which depended on `@/colors`). Tones map to the
   * semantic state tokens (`--color-state-*`) so light/dark are handled by the
   * theme. Use a badge for short status words ("actief", "3 nieuw"), not for
   * sentence-length messaging — reach for `AppAlert` there.
   */
  import Icon from '@/components/Icon';
  import { computed } from 'vue';

  defineOptions({ name: 'AppBadge' });

  const props = defineProps({
    /** Visible text when no default slot is provided. */
    label: { type: String, default: '' },
    /** Semantic colour. */
    tone: {
      type: String,
      default: 'neutral',
      validator: (v) =>
        ['neutral', 'brand', 'success', 'warning', 'danger', 'info'].includes(v),
    },
    /** Fill style. */
    variant: {
      type: String,
      default: 'soft',
      validator: (v) => ['soft', 'solid', 'outline'].includes(v),
    },
    /** sm (default) or md. */
    size: {
      type: String,
      default: 'sm',
      validator: (v) => ['sm', 'md'].includes(v),
    },
    /** Optional leading mdi icon path. */
    icon: { type: String, default: '' },
  });

  // Per-tone class triples keyed by variant. Neutral is special-cased because
  // the theme exposes no `theme-neutral` colour — it uses the text/border/bg
  // surface tokens instead.
  const TONE_CLASSES = {
    neutral: {
      soft: 'bg-theme-bg-subtle text-theme-text-secondary',
      solid: 'bg-theme-text-secondary text-theme-surface',
      outline: 'border border-theme-border text-theme-text-secondary',
    },
    brand: {
      soft: 'bg-theme-brand/10 text-theme-brand',
      solid: 'bg-theme-brand text-theme-text-inverse',
      outline: 'border border-theme-brand/40 text-theme-brand',
    },
    success: {
      soft: 'bg-theme-success/10 text-theme-success',
      solid: 'bg-theme-success text-theme-text-inverse',
      outline: 'border border-theme-success/40 text-theme-success',
    },
    warning: {
      soft: 'bg-theme-warning/10 text-theme-warning',
      solid: 'bg-theme-warning text-theme-text-inverse',
      outline: 'border border-theme-warning/40 text-theme-warning',
    },
    danger: {
      soft: 'bg-theme-danger/10 text-theme-danger',
      solid: 'bg-theme-danger text-theme-text-inverse',
      outline: 'border border-theme-danger/40 text-theme-danger',
    },
    info: {
      soft: 'bg-theme-info/10 text-theme-info',
      solid: 'bg-theme-info text-theme-text-inverse',
      outline: 'border border-theme-info/40 text-theme-info',
    },
  };

  const toneVariantClass = computed(
    () => (TONE_CLASSES[props.tone] || TONE_CLASSES.neutral)[props.variant]
  );

  const sizeClass = computed(() =>
    props.size === 'md' ? 'gap-1.5 px-2.5 py-1 text-xs' : 'gap-1 px-2 py-0.5 text-[10px]'
  );
</script>

<style scoped>
  .app-badge {
    @apply inline-flex items-center whitespace-nowrap rounded-full font-semibold leading-none;
  }
</style>
